(() => {
  if (window.customElements.get('product-card-media')) return;

  const CART_LOADING_ID = 'product-card-cart-loading';
  let cartLoadingTimer = 0;

  const mountGlobalLoadingIcon = (spinner) => {
    const template = document.getElementById('global-loading-icon-template');
    if (!template?.content) return;
    spinner.append(template.content.cloneNode(true));
  };

  const preventOverlayScroll = (event) => {
    event.preventDefault();
  };

  const hideCardCartLoading = () => {
    const overlay = document.getElementById(CART_LOADING_ID);
    window.clearTimeout(cartLoadingTimer);
    cartLoadingTimer = 0;
    document.documentElement.classList.remove('is-product-card-cart-loading');
    if (!overlay) return;
    overlay.classList.remove('is-visible');
    overlay.setAttribute('aria-hidden', 'true');
  };

  const showCardCartLoading = (label) => {
    let overlay = document.getElementById(CART_LOADING_ID);
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = CART_LOADING_ID;
      overlay.className = 'product-card-cart-loading';
      overlay.setAttribute('role', 'status');
      overlay.setAttribute('aria-live', 'polite');
      overlay.setAttribute('aria-hidden', 'true');
      const spinner = document.createElement('span');
      spinner.className = 'product-card-cart-loading__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      mountGlobalLoadingIcon(spinner);
      const text = document.createElement('span');
      text.className = 'sr-only';
      text.textContent = label || 'Loading';
      overlay.append(spinner, text);
      overlay.addEventListener('wheel', preventOverlayScroll, { passive: false });
      overlay.addEventListener('touchmove', preventOverlayScroll, { passive: false });
      document.body.append(overlay);
    }
    window.clearTimeout(cartLoadingTimer);
    overlay.classList.add('is-visible');
    overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('is-product-card-cart-loading');
    cartLoadingTimer = window.setTimeout(hideCardCartLoading, 15000);
  };

  document.addEventListener('cart:change', hideCardCartLoading, { capture: true });
  document.addEventListener('cart:error', hideCardCartLoading);

  const sameColor = (left, right) => {
    if (!left || !right) return false;
    return String(left).trim().toLowerCase() === String(right).trim().toLowerCase();
  };

  const blurInside = (root) => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && root?.contains(active)) active.blur();
  };

  class ProductCardMedia extends HTMLElement {
    connectedCallback() {
      this.index = 0;
      this.activeColor = this.dataset.activeColor || '';
      this.featuredMediaId = this.dataset.featuredMediaId || '';
      this.carouselEnabled = this.dataset.carousel !== 'false';
      this.figure = this.closest('.product-card__figure');
      this.narrowQuery = window.matchMedia('(max-width: 699px)');
      this.canHover = window.matchMedia('(pointer: fine)').matches;
      this.wrapper = this.querySelector('.swiper-wrapper');
      this.slides = Array.from(this.querySelectorAll('.product-card__slide'));
      this.hold = document.createDocumentFragment();
      this.onEnter = () => {
        if (!this.carouselEnabled || this.narrowQuery.matches) return;
        if (this.visibleSlides().length < 2) return;
        this.clearReset();
        this.show(1, 0);
        this.classList.add('is-hovering');
      };
      this.onLeave = () => {
        blurInside(this.figure);
        this.classList.remove('is-hovering');
        this.scheduleReset();
      };
      this.onSwatchChange = (event) => {
        const color = event.target.getAttribute('data-option-value');
        let featuredMediaId = '';
        try {
          const media = JSON.parse(event.target.getAttribute('data-variant-media') || 'null');
          featuredMediaId = media?.id ? String(media.id) : '';
        } catch (error) {
          featuredMediaId = '';
        }
        this.setColor(color || this.dataset.activeColor || '', featuredMediaId);
      };
      this.onNarrowChange = () => {
        if (!this.carouselEnabled) return;
        this.syncTouch();
        if (this.narrowQuery.matches) this.show(0);
        this.syncNav();
      };

      if (this.carouselEnabled) {
        this.querySelector('[data-nav="prev"]')?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.narrowQuery.matches) return;
          this.go(-1);
        });
        this.querySelector('[data-nav="next"]')?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.narrowQuery.matches) return;
          this.go(1);
        });

        if (this.canHover) {
          this.figure?.addEventListener('mouseenter', this.onEnter);
          this.figure?.addEventListener('mouseleave', this.onLeave);
        }

        this.narrowQuery.addEventListener('change', this.onNarrowChange);
      }

      this.closest('product-card')
        ?.querySelector('.product-card__swatch-list')
        ?.addEventListener('change', this.onSwatchChange);

      this.syncSlides();
      this.syncCover();
      if (this.carouselEnabled) {
        this.initSwiper();
        this.syncNav();
      }
    }

    disconnectedCallback() {
      this.clearReset();
      this.figure?.removeEventListener('mouseenter', this.onEnter);
      this.figure?.removeEventListener('mouseleave', this.onLeave);
      this.closest('product-card')
        ?.querySelector('.product-card__swatch-list')
        ?.removeEventListener('change', this.onSwatchChange);
      this.narrowQuery?.removeEventListener('change', this.onNarrowChange);
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    waitForSwiper() {
      if (window.Swiper) return Promise.resolve();
      return new Promise((resolve) => {
        let tries = 0;
        const tick = () => {
          if (window.Swiper || tries > 40) {
            resolve();
            return;
          }
          tries += 1;
          window.setTimeout(tick, 50);
        };
        tick();
      });
    }

    visibleSlides() {
      const source = this.wrapper
        ? Array.from(this.wrapper.querySelectorAll(':scope > .product-card__slide'))
        : this.slides;
      return source.filter((slide) => {
        const image = slide.querySelector('.product-card__image');
        return image && this.matchesColor(image);
      });
    }

    matchesColor(image) {
      const group = image.getAttribute('data-color-group');
      if (!this.activeColor) return true;
      if (!group) return false;
      if (group === '*') return true;
      return sameColor(group, this.activeColor);
    }

    isFeaturedImage(image) {
      if (!image) return false;
      if (this.featuredMediaId && image.getAttribute('data-media-id') === String(this.featuredMediaId)) {
        return true;
      }
      if (this.featuredMediaId) return false;
      return image.getAttribute('data-variant-featured') === 'true';
    }

    featuredImage() {
      if (this.featuredMediaId) {
        for (const slide of this.slides) {
          const image = slide.querySelector('.product-card__image');
          if (this.isFeaturedImage(image)) return image;
        }
      }
      return this.images().find((image) => this.isFeaturedImage(image)) || this.images()[0];
    }

    images() {
      return this.visibleSlides()
        .map((slide) => slide.querySelector('.product-card__image'))
        .filter(Boolean);
    }

    syncSlides() {
      if (!this.wrapper) return;
      const featured = [];
      const others = [];
      this.slides.forEach((slide) => {
        const image = slide.querySelector('.product-card__image');
        const match = image && (this.matchesColor(image) || this.isFeaturedImage(image));
        slide.hidden = !match;
        if (!match) {
          this.hold.append(slide);
          return;
        }
        if (this.isFeaturedImage(image)) featured.push(slide);
        else others.push(slide);
      });
      featured.concat(others).forEach((slide) => this.wrapper.append(slide));
    }

    syncCover() {
      const first = this.featuredImage();
      const href = this.querySelector('.product-card__media-link')?.getAttribute('href') || '';
      let cover = this.querySelector('.product-card__cover');
      if (!first) {
        cover?.remove();
        return;
      }
      if (!cover) {
        cover = document.createElement('a');
        cover.className = 'product-card__cover';
        this.prepend(cover);
      }
      cover.setAttribute('href', href);
      cover.replaceChildren(first.cloneNode(true));
    }

    async initSwiper() {
      const root = this.querySelector('.product-card__swiper');
      if (!root || this.swiper) return;
      await this.waitForSwiper();
      if (!this.isConnected || typeof window.Swiper !== 'function') return;
      this.swiper = new window.Swiper(root, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,
        nested: true,
        loop: this.visibleSlides().length > 1,
        watchOverflow: true,
        allowTouchMove: this.canSwipe(),
        preventClicks: false,
        preventClicksPropagation: false,
        touchMoveStopPropagation: true,
        on: {
          slideChange: (swiper) => {
            this.index = swiper.realIndex;
            this.images().forEach((image, imageIndex) => {
              image.classList.toggle('is-active', imageIndex === this.index);
            });
          },
        },
      });
    }

    canSwipe() {
      return this.narrowQuery.matches && this.visibleSlides().length > 1;
    }

    syncTouch() {
      if (this.swiper) this.swiper.allowTouchMove = this.canSwipe();
    }

    clearReset() {
      if (this.resetTimer) {
        window.clearTimeout(this.resetTimer);
        this.resetTimer = null;
      }
      const cover = this.querySelector('.product-card__cover');
      if (cover && this.onCoverFadeEnd) {
        cover.removeEventListener('transitionend', this.onCoverFadeEnd);
      }
      this.onCoverFadeEnd = null;
    }

    scheduleReset() {
      this.clearReset();
      const cover = this.querySelector('.product-card__cover');
      const finish = () => {
        this.clearReset();
        if (this.classList.contains('is-hovering')) return;
        this.show(0, 0);
      };
      this.onCoverFadeEnd = (event) => {
        if (event.target !== cover || event.propertyName !== 'opacity') return;
        finish();
      };
      cover?.addEventListener('transitionend', this.onCoverFadeEnd);
      this.resetTimer = window.setTimeout(finish, 400);
    }

    setColor(color, featuredMediaId) {
      this.activeColor = color || '';
      this.dataset.activeColor = this.activeColor;
      if (featuredMediaId) {
        this.featuredMediaId = String(featuredMediaId);
      } else {
        const fallback = this.slides
          .map((slide) => slide.querySelector('.product-card__image'))
          .find((image) => image && this.matchesColor(image) && image.getAttribute('data-variant-featured') === 'true');
        this.featuredMediaId = fallback?.getAttribute('data-media-id') || '';
      }
      this.clearReset();
      this.classList.remove('is-hovering');
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.syncSlides();
      this.syncCover();
      this.index = 0;
      if (this.carouselEnabled) {
        this.initSwiper();
        this.syncTouch();
        this.syncNav();
      }
    }

    syncNav() {
      const showNav = !this.narrowQuery.matches && this.images().length > 1;
      this.querySelectorAll('.product-card__nav').forEach((button) => {
        button.hidden = !showNav;
      });
    }

    go(step) {
      if (this.visibleSlides().length < 2) return;
      if (step > 0) this.swiper?.slideNext();
      else this.swiper?.slidePrev();
    }

    show(index, speed) {
      const total = this.visibleSlides().length;
      if (!total) return;
      this.index = ((index % total) + total) % total;
      const duration = speed == null ? this.swiper?.params?.speed ?? 300 : speed;
      if (this.swiper?.params?.loop && typeof this.swiper.slideToLoop === 'function') {
        this.swiper.slideToLoop(this.index, duration);
        return;
      }
      this.swiper?.slideTo(this.index, duration);
    }
  }

  class ProductCardQuickAdd extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('product-card');
      this.figure = this.closest('.product-card__figure');
      this.idInput = this.querySelector('input[name="id"]');
      this.hasSizes = this.dataset.hasSizes === 'true';
      this.sizePosition = Number(this.dataset.sizePosition || 0);
      this.colorPosition = Number(this.dataset.colorPosition || 0);
      this.variants = this.readVariants();
      this.sheetQuery = window.matchMedia('(max-width: 699px)');
      this.dialog = this.card?.querySelector('.product-card__size-dialog');
      this.onLeave = () => {
        blurInside(this.figure);
        this.sizeButtons().forEach((button) => button.classList.remove('is-selected'));
      };
      this.onSwatchChange = () => {
        this.reset();
        this.syncSizes();
        this.syncDefaultVariant();
      };
      this.onCompactClick = (event) => {
        const compact = event.target.closest('[data-compact-add]');
        if (!compact || !this.card?.contains(compact)) return;
        if (compact.getAttribute('aria-controls')) return;
        event.preventDefault();
        event.stopPropagation();
        if (!this.hasSizes) {
          this.submitAdd();
          return;
        }
        this.toggleSheet();
      };
      this.onOutsideClick = (event) => {
        if (!this.classList.contains('is-compact-open')) return;
        if (this.contains(event.target)) return;
        if (event.target.closest('[data-compact-add]') && this.card?.contains(event.target)) return;
        this.closeSheet();
      };
      this.onDialogClick = (event) => {
        if (event.target === this.dialog || event.target.closest('[data-size-close]')) {
          event.preventDefault();
          this.closeSheet();
        }
      };
      this.onSheetChange = () => {
        if (!this.sheetQuery.matches) this.closeSheet();
      };
      this.onFormSubmit = () => {
        const form = this.form();
        if (!form?.checkValidity()) return;
        if (form.querySelector('[type="submit"][aria-busy="true"]')) return;
        showCardCartLoading(this.dataset.loadingLabel);
      };

      this.card?.addEventListener('click', this.onCompactClick);
      document.addEventListener('click', this.onOutsideClick);
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.sheetQuery.addEventListener('change', this.onSheetChange);
      this.form()?.addEventListener('submit', this.onFormSubmit, { capture: true });

      this.sizeButtons().forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (button.disabled || button.classList.contains('is-disabled')) return;
          this.addSize(button.dataset.size);
        });
      });

      this.figure?.addEventListener('mouseleave', this.onLeave);
      this.card?.querySelector('.product-card__swatch-list')?.addEventListener('change', this.onSwatchChange);
      this.syncSizes();
      this.syncDefaultVariant();
    }

    disconnectedCallback() {
      this.figure?.removeEventListener('mouseleave', this.onLeave);
      this.card?.querySelector('.product-card__swatch-list')?.removeEventListener('change', this.onSwatchChange);
      this.card?.removeEventListener('click', this.onCompactClick);
      document.removeEventListener('click', this.onOutsideClick);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.sheetQuery?.removeEventListener('change', this.onSheetChange);
      this.form()?.removeEventListener('submit', this.onFormSubmit, { capture: true });
      this.closeSheet();
    }

    sizeButtons() {
      const root = this.dialog?.querySelector('[data-size]') ? this.dialog : this.card || this;
      return Array.from(root.querySelectorAll('[data-size]'));
    }

    isSheetMode() {
      return this.sheetQuery.matches;
    }

    isOpen() {
      return this.classList.contains('is-compact-open') || this.dialog?.classList.contains('is-open');
    }

    toggleSheet() {
      if (this.isOpen()) {
        this.closeSheet();
        return;
      }
      this.openSheet();
    }

    sheet() {
      return this.dialog?.querySelector('.product-card__size-sheet');
    }

    playSheet(sheet, from, to) {
      this.sheetAnimation?.cancel();
      this.sheetAnimation = sheet.animate(
        [{ transform: `translate3d(0, ${from}px, 0)` }, { transform: `translate3d(0, ${to}px, 0)` }],
        { duration: 450, easing: 'cubic-bezier(0.86, 0, 0.07, 1)', fill: 'forwards' }
      );
      return this.sheetAnimation.finished.catch(() => {});
    }

    openSheet() {
      if (this.isSheetMode()) {
        const panel = this.querySelector('.product-card__size-panel');
        const sheet = this.sheet();
        const body = this.dialog?.querySelector('.product-card__size-body');
        if (!this.dialog || !panel || !sheet || !body) return;
        document.querySelectorAll('product-card-quick-add').forEach((item) => {
          if (item !== this) item.closeSheet?.();
        });
        this.dialogHome = this.dialog.parentElement;
        this.panelHome = panel.parentElement;
        body.append(panel);
        document.body.append(this.dialog);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        sheet.style.transform = reduceMotion ? 'translate3d(0, 0, 0)' : 'translate3d(0, 100%, 0)';
        this.dialog.hidden = false;
        this.dialog.classList.add('is-open');
        if (reduceMotion) return;
        const distance = sheet.offsetHeight;
        sheet.style.transform = `translate3d(0, ${distance}px, 0)`;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.playSheet(sheet, distance, 0);
          });
        });
        return;
      }
      this.classList.add('is-compact-open');
    }

    closeSheet() {
      if (this.dialog?.classList.contains('is-open')) {
        if (this.sheetClosing) return;
        const sheet = this.sheet();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!sheet || reduceMotion) {
          this.teardownSheet();
          return;
        }
        this.sheetClosing = true;
        this.dialog.classList.add('is-closing');
        this.playSheet(sheet, 0, sheet.offsetHeight).then(() => {
          this.teardownSheet();
        });
        return;
      }
      this.teardownSheet();
      this.classList.remove('is-compact-open');
    }

    teardownSheet() {
      this.sheetAnimation?.cancel();
      this.sheetAnimation = null;
      this.sheetClosing = false;
      const sheet = this.sheet();
      const panel = this.dialog?.querySelector('.product-card__size-panel') || this.querySelector('.product-card__size-panel');
      if (sheet) sheet.style.transform = '';
      if (panel && this.panelHome && panel.parentElement !== this.panelHome) {
        this.panelHome.append(panel);
      }
      this.panelHome = null;
      if (this.dialog) {
        this.dialog.classList.remove('is-open', 'is-closing');
        this.dialog.hidden = true;
        if (this.dialogHome && this.dialog.parentElement !== this.dialogHome) {
          this.dialogHome.append(this.dialog);
        }
      }
      this.dialogHome = null;
    }

    readVariants() {
      const payload = this.querySelector('[data-variants]');
      if (!payload) return [];
      try {
        return JSON.parse(payload.textContent);
      } catch (error) {
        return [];
      }
    }

    getSelectedColor() {
      if (!this.colorPosition) return null;
      const checked = this.card?.querySelector('.product-card__swatch-list [type="radio"]:checked');
      if (checked?.getAttribute('data-option-value')) {
        return checked.getAttribute('data-option-value');
      }
      const variantId = checked?.getAttribute('data-variant-id') || this.idInput?.value;
      const variant = this.variants.find((item) => String(item.id) === String(variantId));
      return variant?.options?.[this.colorPosition - 1] || null;
    }

    findVariant(size) {
      const color = this.getSelectedColor();
      return this.variants.find((variant) => {
        const sizeMatch = !this.sizePosition || !size || variant.options?.[this.sizePosition - 1] === size;
        const colorMatch = !this.colorPosition || !color || variant.options?.[this.colorPosition - 1] === color;
        return sizeMatch && colorMatch;
      });
    }

    addSize(size) {
      const form = this.form();
      if (form?.querySelector('[type="submit"][aria-busy="true"]')) return;

      const variant = this.findVariant(size);
      if (!variant?.available) return;
      if (this.idInput) this.idInput.value = variant.id;
      this.sizeButtons().forEach((button) => {
        button.classList.toggle('is-selected', button.dataset.size === size);
      });
      this.closeSheet();
      form?.requestSubmit();
    }

    submitAdd() {
      const form = this.form();
      if (form?.querySelector('[type="submit"][aria-busy="true"]')) return;
      form?.requestSubmit();
    }

    form() {
      return this.querySelector('form[action*="/cart/add"]');
    }

    reset() {
      this.closeSheet();
      this.sizeButtons().forEach((button) => button.classList.remove('is-selected'));
    }

    syncSizes() {
      this.sizeButtons().forEach((button) => {
        const variant = this.findVariant(button.dataset.size);
        // 当前颜色没有这条尺码变体就隐藏；变体存在但售罄才划线
        if (!variant) {
          button.hidden = true;
          button.disabled = true;
          button.classList.remove('is-disabled', 'is-selected');
          return;
        }
        button.hidden = false;
        const available = Boolean(variant.available);
        button.classList.toggle('is-disabled', !available);
        button.disabled = !available;
      });
    }

    syncDefaultVariant() {
      if (this.hasSizes || !this.idInput) return;
      const color = this.getSelectedColor();
      const variant =
        this.variants.find((item) => {
          const colorMatch = !this.colorPosition || !color || item.options?.[this.colorPosition - 1] === color;
          return colorMatch && item.available;
        }) || this.variants[0];
      if (variant) this.idInput.value = variant.id;
    }
  }

  window.customElements.define('product-card-media', ProductCardMedia);
  window.customElements.define('product-card-quick-add', ProductCardQuickAdd);
})();

(() => {
  const STYLE_FLAG = 'data-product-card-wishlist';
  const TOOLTIP_FLAG = 'data-product-card-wishlist-notip';
  const COMMIT_FLAG = 'data-product-card-wishlist-committed';
  const PENDING_FLAG = 'data-product-card-wishlist-pending';
  const POP_FLAG = 'data-product-card-wishlist-pop';
  const COMMIT_BOUND = 'data-product-card-wishlist-commit';
  const POP_DURATION = 400;
  const PENDING_TIMEOUT = 4000;
  const HOST_VARS = {
    '--xb-wishlist-icon-background-color': 'transparent',
    '--xb-wishlist-icon-background-color-active': 'transparent',
    '--xb-wishlist-background-color': 'transparent',
    '--xb-wishlist-background-color-active': 'transparent',
    '--xb-wishlist-icon-border-radius': '0',
    '--xb-wishlist-icon-color': '#1c1c1c',
    '--xb-wishlist-icon-active-color': '#1c1c1c',
  };
  const SHADOW_CSS = `
    :host,
    :host(:hover),
    .xb-wishlist-button-collection,
    .xb-wishlist-button-collection:hover {
      width: var(--xb-wishlist-hit, auto) !important;
      height: var(--xb-wishlist-hit, auto) !important;
      max-width: var(--xb-wishlist-hit, calc(var(--xb-wishlist-icon-size, 20px) + var(--xb-wishlist-icon-padding, 4px) * 2)) !important;
      max-height: var(--xb-wishlist-hit, calc(var(--xb-wishlist-icon-size, 20px) + var(--xb-wishlist-icon-padding, 4px) * 2)) !important;
      pointer-events: none !important;
      background: transparent !important;
      background-color: transparent !important;
      box-shadow: none !important;
      transform: none !important;
      transition: none !important;
    }

    .xb-wishlist-button-collection[xb-placement="top-right"] {
      position: relative !important;
      top: auto !important;
      right: auto !important;
      left: auto !important;
      bottom: auto !important;
    }

    .xb-wishlist-button-collection__add,
    .xb-wishlist-button-collection__remove,
    .xb-wishlist-button-collection__add:hover,
    .xb-wishlist-button-collection__remove:hover,
    button,
    button:hover {
      pointer-events: auto !important;
      background: transparent !important;
      background-color: transparent !important;
      box-shadow: none !important;
      transform: none !important;
      animation: none !important;
      filter: none !important;
    }

    .xb-wishlist-button-collection__add-icon,
    .xb-wishlist-button-collection__remove-icon,
    .xb-wishlist-button-collection__add-icon svg,
    .xb-wishlist-button-collection__remove-icon svg,
    svg {
      width: var(--xb-wishlist-icon-size, 20px) !important;
      height: var(--xb-wishlist-icon-size, 20px) !important;
      flex: 0 0 auto;
      animation: none !important;
      transform: none !important;
      transition: none !important;
    }

    @media (max-width: 699px) {
      :host {
        pointer-events: auto !important;
      }

      .xb-wishlist-button-collection,
      .xb-wishlist-button-collection__add,
      .xb-wishlist-button-collection__remove,
      button:not(.product-card-wishlist-hit) {
        pointer-events: none !important;
        box-sizing: border-box !important;
        width: 100% !important;
        height: 100% !important;
        max-width: none !important;
        max-height: none !important;
        min-width: 100% !important;
        min-height: 100% !important;
        display: flex !important;
        align-items: flex-start !important;
        justify-content: flex-end !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      button:not(.product-card-wishlist-hit) {
        padding: var(--xb-wishlist-icon-padding, 4px) !important;
      }
    }
  `;

  const narrowQuery = window.matchMedia('(max-width: 699px)');

  const cardMetrics = (button) => {
    const card = button.closest('product-card');
    const cs = card ? getComputedStyle(card) : null;
    const icon = (cs?.getPropertyValue('--product-card-wishlist-icon') || '20px').trim();
    const pad = (cs?.getPropertyValue('--product-card-wishlist-pad') || '4px').trim();
    const hit =
      (cs?.getPropertyValue('--product-card-wishlist-hit') || '').trim() || `calc(${icon} + ${pad} * 2)`;
    return { icon, pad, hit };
  };

  const applySizeVars = (el, icon, pad, hit) => {
    el.style.setProperty('--xb-wishlist-icon-size', icon, 'important');
    el.style.setProperty('--xb-wishlist-icon-padding', pad, 'important');
    el.style.setProperty('--xb-wishlist-hit', hit, 'important');
  };

  const applyHostVars = (button, metrics) => {
    const { icon, pad, hit } = metrics;
    Object.entries(HOST_VARS).forEach(([name, value]) => {
      button.style.setProperty(name, value, 'important');
    });
    applySizeVars(button, icon, pad, hit);
    button.style.setProperty('background', 'transparent', 'important');
    button.style.setProperty('background-color', 'transparent', 'important');
    button.style.setProperty('box-shadow', 'none', 'important');
    button.style.setProperty('width', hit, 'important');
    button.style.setProperty('height', hit, 'important');
    button.style.setProperty('max-width', hit, 'important');
    button.style.setProperty('max-height', hit, 'important');
    button.style.setProperty('min-width', hit, 'important');
    button.style.setProperty('min-height', hit, 'important');
    button.style.setProperty('pointer-events', narrowQuery.matches ? 'auto' : 'none', 'important');
  };

  const pinInner = (button, root, metrics) => {
    const { icon, pad, hit } = metrics;
    const inner = root.querySelector('.xb-wishlist-button-collection');
    [inner, ...root.querySelectorAll('button')].filter(Boolean).forEach((el) => {
      Object.entries(HOST_VARS).forEach(([name, value]) => {
        el.style.setProperty(name, value, 'important');
      });
      applySizeVars(el, icon, pad, hit);
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('background-color', 'transparent', 'important');
      el.style.setProperty('box-shadow', 'none', 'important');
    });
    if (!inner) return;
    inner.style.setProperty('position', 'relative', 'important');
    inner.style.setProperty('top', 'auto', 'important');
    inner.style.setProperty('right', 'auto', 'important');
    inner.style.setProperty('left', 'auto', 'important');
    inner.style.setProperty('bottom', 'auto', 'important');
    inner.style.setProperty('transform', 'none', 'important');
  };

  const styleButton = (button, metrics) => {
    applyHostVars(button, metrics);
    if (!window.customElements.get('xb-wishlist-button-collection')) return false;
    const root = button.shadowRoot || button;
    if (!root.querySelector('.xb-wishlist-button-collection')) return false;
    if (button.shadowRoot) {
      let style = button.shadowRoot.querySelector(`[${STYLE_FLAG}]`);
      if (!style) {
        style = document.createElement('style');
        style.setAttribute(STYLE_FLAG, '');
        button.shadowRoot.append(style);
      }
      style.textContent = SHADOW_CSS;
    }
    pinInner(button, root, metrics);
    return true;
  };

  const pending = new Set();
  const listObservers = new WeakMap();
  let retrying = false;

  const isImageOnlyList = (list) => {
    if (!(list instanceof HTMLElement)) return false;
    if (list.getAttribute('desktop-layout') === 'thumbnails') return true;
    return narrowQuery.matches && list.getAttribute('mobile-items-per-row') === '3';
  };

  const placeButton = (button) => {
    const card = button.closest('product-card');
    const figure = card?.querySelector('.product-card__figure');
    if (figure && button.parentElement !== figure) figure.append(button);
  };

  /* 应用把 tooltip 的 mousemove 监听挂在按钮外层 span 上，在宿主捕获阶段截断即可让提示不再出现 */
  const muteTooltip = (button) => {
    if (button.hasAttribute(TOOLTIP_FLAG)) return;
    button.setAttribute(TOOLTIP_FLAG, '');
    button.addEventListener('mousemove', (event) => event.stopPropagation(), true);
  };

  const HIT_CLASS = 'product-card-wishlist-hit';

  const findAppControl = (button) =>
    button.querySelector('.xb-wishlist-button-collection__add button') ||
    button.querySelector('.xb-wishlist-button-collection__remove button') ||
    button.querySelector('button:not(.product-card-wishlist-hit)') ||
    button.querySelector('.xb-wishlist-button-collection__add') ||
    button.querySelector('.xb-wishlist-button-collection__remove');

  /* 自己盖一层可点热区：拦住进详情，再转发给应用按钮。
     应用监听若挂在 document 上，宿主 stopPropagation 会让收藏失效；
     iOS 全透明节点还会把点击漏给下面的商品链接 */
  const bindHitProxy = (button) => {
    let hit = button.querySelector('.' + HIT_CLASS);
    if (!narrowQuery.matches) {
      hit?.remove();
      return;
    }
    if (hit) return;

    hit = document.createElement('button');
    hit.type = 'button';
    hit.className = HIT_CLASS;
    hit.setAttribute('aria-hidden', 'true');
    hit.tabIndex = -1;

    const stopNav = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    };

    let armed = false;
    hit.addEventListener('pointerdown', (event) => {
      stopNav(event);
      armed = true;
    });
    /* Swiper 听的是 touchstart，只拦 pointerdown 拦不住轮播把这次点改派到商品链接 */
    hit.addEventListener(
      'touchstart',
      (event) => {
        stopNav(event);
        armed = true;
      },
      { passive: false }
    );
    hit.addEventListener('pointercancel', () => {
      armed = false;
    });
    hit.addEventListener('pointerup', (event) => {
      stopNav(event);
      if (!armed) return;
      armed = false;
      findAppControl(button)?.click();
    });
    hit.addEventListener('click', stopNav);
    hit.addEventListener('touchend', stopNav, { passive: false });

    button.append(hit);
  };

  const NAV_GUARD = 'data-product-card-wishlist-nav-guard';

  /* 点在心上但事件被 Swiper / 商品图链接改派时，按坐标拦住进详情并补一次收藏 */
  const bindNavGuard = (card) => {
    if (!(card instanceof HTMLElement) || card.hasAttribute(NAV_GUARD)) return;
    card.setAttribute(NAV_GUARD, '');
    card.addEventListener(
      'click',
      (event) => {
        if (!narrowQuery.matches) return;
        const wish = card.querySelector('xb-wishlist-button-collection');
        if (!wish || wish.hidden) return;
        const rect = wish.getBoundingClientRect();
        const inWish =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inWish) return;
        event.preventDefault();
        if (event.target.closest('xb-wishlist-button-collection')) return;
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
        findAppControl(wish)?.click();
      },
      true
    );
  };

  const ICON_FILL = {
    add: '.xb-wishlist-button-collection__add-icon-fill',
    remove: '.xb-wishlist-button-collection__remove-icon-fill',
  };
  /* 点击时要冻结的是「点击后会变成的样子」：未收藏点下去是实心，已收藏点下去是描边 */
  const TARGET_ICON = {
    add: '.xb-wishlist-button-collection__add-icon-fill svg',
    remove: '.xb-wishlist-button-collection__remove-icon-outline svg',
  };
  const FREEZE_CLASS = 'product-card-wishlist-freeze';

  const wishlistState = (button) => {
    if (button.querySelector('.xb-wishlist-button-collection__remove')) return 'remove';
    if (button.querySelector('.xb-wishlist-button-collection__add')) return 'add';
    return '';
  };

  const clearFreeze = (button) => {
    button.querySelectorAll('.' + FREEZE_CLASS).forEach((layer) => layer.remove());
  };

  /* 图标的描边与填充色来自应用针对自身 class 的规则，副本脱离那个上下文会掉回默认黑，
     所以逐个节点把算出来的绘制色写成行内样式 */
  const cloneWithPaint = (svg) => {
    const clone = svg.cloneNode(true);
    const sources = [svg, ...svg.querySelectorAll('*')];
    const targets = [clone, ...clone.querySelectorAll('*')];
    sources.forEach((source, index) => {
      const cs = getComputedStyle(source);
      const target = targets[index];
      target.style.fill = cs.fill;
      target.style.stroke = cs.stroke;
      target.style.strokeWidth = cs.strokeWidth;
    });
    return clone;
  };

  const mountButtonLoadingIcon = (layer) => {
    const template = document.getElementById('button-loading-icon-template');
    const icon = template?.content?.firstElementChild;
    if (!icon) return false;
    layer.append(icon.cloneNode(true));
    return true;
  };

  /* 添加：请求期间用主题转圈盖住应用自带 loading，成功后再播爱心跳动。
     取消：仍冻结成点击后的空心。挂在宿主上，避免应用重建子节点时被清掉 */
  const showPendingLayer = (button, state) => {
    clearFreeze(button);
    const layer = document.createElement('span');
    layer.setAttribute('aria-hidden', 'true');

    if (state === 'add' && mountButtonLoadingIcon(layer)) {
      layer.className = `${FREEZE_CLASS} ${FREEZE_CLASS}--loading`;
      button.append(layer);
      return;
    }

    const source = button.querySelector(TARGET_ICON[state]);
    if (!source) return;
    layer.className = FREEZE_CLASS;
    layer.append(cloneWithPaint(source));
    button.append(layer);
  };

  /* 悬停预览画的是「点击后会变成的样子」，而应用要等接口回来才就地切状态。
     添加时 pending 显示转圈；取消时冻结空心。应用确认后落到真实状态并回弹一次；
     之后再锁住预览（committed）到指针移出，否则残留悬停会把图标又画回点击前的样子 */
  const trackCommit = (button) => {
    if (button.hasAttribute(COMMIT_BOUND)) return;
    button.setAttribute(COMMIT_BOUND, '');

    let state = wishlistState(button);
    let popTimer = 0;
    let pendingTimer = 0;

    button.addEventListener(
      'click',
      () => {
        showPendingLayer(button, wishlistState(button));
        button.setAttribute(COMMIT_FLAG, '');
        button.setAttribute(PENDING_FLAG, '');
        clearTimeout(pendingTimer);
        /* 接口失败时不能一直转圈/冻结，超时后放回真实状态 */
        pendingTimer = setTimeout(() => {
          button.removeAttribute(PENDING_FLAG);
          clearFreeze(button);
        }, PENDING_TIMEOUT);
      },
      true
    );
    button.addEventListener('pointerleave', () => button.removeAttribute(COMMIT_FLAG));

    new MutationObserver(() => {
      const next = wishlistState(button);
      if (!next || next === state) return;
      /* 应用切状态时会先把图标换成 loading 转圈，等心形真正渲染回来再收尾，
         否则回弹动画放在转圈图标上，用户看到心形时动画已经结束 */
      if (!button.querySelector(ICON_FILL[next])) return;
      state = next;
      clearTimeout(pendingTimer);
      button.removeAttribute(PENDING_FLAG);
      clearFreeze(button);
      /* 先移除再强制重排，连点时同名动画才会重新播放 */
      button.removeAttribute(POP_FLAG);
      void button.offsetWidth;
      button.setAttribute(POP_FLAG, '');
      clearTimeout(popTimer);
      popTimer = setTimeout(() => button.removeAttribute(POP_FLAG), POP_DURATION);
    }).observe(button, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  };

  const hideWishlist = (button) => {
    placeButton(button);
    button.hidden = true;
    button.setAttribute('aria-hidden', 'true');
    button.style.setProperty('display', 'none', 'important');
    button.style.setProperty('opacity', '0', 'important');
    button.style.setProperty('visibility', 'hidden', 'important');
    button.style.setProperty('pointer-events', 'none', 'important');
  };

  const showWishlist = (button) => {
    button.hidden = false;
    button.removeAttribute('aria-hidden');
    button.style.removeProperty('display');
    button.style.removeProperty('opacity');
    button.style.removeProperty('visibility');
    button.style.removeProperty('pointer-events');
  };

  const retryPending = () => {
    retrying = false;
    const connectedButtons = [];

    pending.forEach((button) => {
      if (!button.isConnected) {
        pending.delete(button);
        return;
      }
      connectedButtons.push(button);
    });

    const buttonsWithMetrics = connectedButtons.map((button) => ({
      button,
      metrics: cardMetrics(button),
    }));

    buttonsWithMetrics.forEach(({ button, metrics }) => {
      if (styleButton(button, metrics)) pending.delete(button);
    });
    if (pending.size) {
      retrying = true;
      requestAnimationFrame(retryPending);
    }
  };

  const queueRetry = () => {
    if (retrying || !pending.size) return;
    retrying = true;
    requestAnimationFrame(retryPending);
  };

  const scan = () => {
    const visibleButtons = [];

    document.querySelectorAll('product-card xb-wishlist-button-collection').forEach((button) => {
      if (isImageOnlyList(button.closest('.product-list'))) {
        hideWishlist(button);
        pending.delete(button);
        return;
      }
      placeButton(button);
      muteTooltip(button);
      bindHitProxy(button);
      bindNavGuard(button.closest('product-card'));
      trackCommit(button);
      showWishlist(button);
      visibleButtons.push(button);
    });

    const buttonsWithMetrics = visibleButtons.map((button) => ({
      button,
      metrics: cardMetrics(button),
    }));

    buttonsWithMetrics.forEach(({ button, metrics }) => {
      if (!styleButton(button, metrics)) pending.add(button);
      else pending.delete(button);
    });
    queueRetry();
  };

  const observeList = (list) => {
    if (!(list instanceof HTMLElement) || listObservers.has(list)) return;
    const observer = new MutationObserver(scan);
    observer.observe(list, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['desktop-layout', 'mobile-items-per-row'],
    });
    listObservers.set(list, observer);
  };

  const observeLists = () => {
    document.querySelectorAll('.product-list').forEach(observeList);
  };

  observeLists();
  scan();
  narrowQuery.addEventListener('change', scan);
  if (window.customElements?.whenDefined) {
    window.customElements.whenDefined('xb-wishlist-button-collection').then(scan).catch(() => {});
  }

  /* 搜索抽屉的结果、无结果推荐是运行时插入的，卡片不在 .product-list 里，
     只盯列表会漏掉这批卡片，心愿按钮会停在应用默认的白色圆底样式。
     应用注入按钮又晚于卡片入场，所以按钮本身也要作为触发条件 */
  const RESCAN_SELECTOR = 'product-card, xb-wishlist-button-collection';
  let scanQueued = false;

  const queueScan = () => {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      scan();
    });
  };

  new MutationObserver((mutations) => {
    let foundList = false;
    let foundCard = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('.product-list') || node.querySelector?.('.product-list')) {
          foundList = true;
        } else if (node.matches?.(RESCAN_SELECTOR) || node.querySelector?.(RESCAN_SELECTOR)) {
          foundCard = true;
        }
      }
    }
    if (!foundList && !foundCard) return;
    if (foundList) observeLists();
    queueScan();
  }).observe(document.body, { childList: true, subtree: true });

  [300, 1000, 2500].forEach((delay) => {
    setTimeout(scan, delay);
  });
})();

(() => {
  const formatMoney = (cents) => {
    if (typeof window.themeFormatMoney === 'function') return window.themeFormatMoney(cents);
    return String(Math.round(Number(cents) || 0) / 100);
  };

  const setAmount = (el, formatted, label) => {
    if (!el) return;
    const sr = document.createElement('span');
    sr.className = 'sr-only';
    sr.textContent = label || '';
    el.replaceChildren(sr, document.createTextNode(formatted));
  };

  const readVariants = (card) => {
    const payload = card.querySelector('[data-card-variants]');
    if (!payload) return [];
    try {
      return JSON.parse(payload.textContent);
    } catch (error) {
      return [];
    }
  };

  const pickVariant = (card, color) => {
    const variants = readVariants(card);
    const colorPosition = Number(card.getAttribute('data-color-position') || 0);
    const strategy = card.getAttribute('data-price-strategy') || 'from_price';
    const matches = variants.filter((item) => {
      if (!colorPosition || !color) return true;
      return item.options?.[colorPosition - 1] === color;
    });
    const pool = matches.length ? matches : variants;
    return pool.reduce((best, item) => {
      if (!best) return item;
      if (strategy === 'from_price') return Number(item.price) < Number(best.price) ? item : best;
      return Number(item.price) > Number(best.price) ? item : best;
    }, null);
  };

  const renderPrice = (card, variant) => {
    if (!variant) return;
    const price = Number(variant.price) || 0;
    const compare = Number(variant.compare_at_price) || 0;
    const onSale = compare > price && price > 0;

    card.setAttribute('data-variant-id', variant.id);
    card.setAttribute('data-catalog-price', String(price));
    if (onSale) card.setAttribute('data-compare-at-price', String(compare));
    else card.removeAttribute('data-compare-at-price');

    const list = card.querySelector('.product-card__price .price-list, .product-card__price price-list');
    const sale = list?.querySelector('sale-price');
    if (!list || !sale) return;

    sale.classList.toggle('text-on-sale', onSale);
    sale.classList.toggle('text-subdued', !onSale);
    setAmount(sale, formatMoney(price), card.getAttribute('data-price-sale-label'));

    let compareEl = list.querySelector('compare-at-price');
    if (onSale) {
      if (!compareEl) {
        compareEl = document.createElement('compare-at-price');
        compareEl.className = 'subheading text-subdued line-through';
        list.append(compareEl);
      }
      setAmount(compareEl, formatMoney(compare), card.getAttribute('data-price-regular-label'));
    } else if (compareEl) {
      compareEl.remove();
    }
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('.product-card__swatch-list [type="radio"]');
    if (!input) return;
    const card = input.closest('product-card');
    if (!card) return;
    const color = input.getAttribute('data-option-value');
    const variant =
      pickVariant(card, color) ||
      {
        id: input.getAttribute('data-variant-id'),
        price: input.getAttribute('data-price'),
        compare_at_price: input.getAttribute('data-compare-at-price'),
      };
    renderPrice(card, variant);
  });
})();
